// ============================================================================
// OSINT Data Layer — vendor-neutral facade dengan adapter pattern.
// ============================================================================
// Operator-gated 2-stage pipeline:
//   Stage 1 (discover):  Find candidate profiles dari nama + foto + (optional) username
//   Stage 2 (enrich):    Pull data dari kandidat yang DIPILIH operator
//
// Adapter:
//   - socialMapper.js → Stage 1 primary (jika viability check OK)
//   - apify.js        → Stage 2 + Stage 1 fallback
//   - brand24.js      → DISABLED, button tampil tapi tidak jalan
// ============================================================================

import { supabase } from '../supabase';
import { socialMapperDiscover } from './adapters/socialMapper';
import { apifyDiscover, apifyEnrich } from './adapters/apify';
import { scoreCandidate } from './scoring';
import { comparePhotosWithSubject } from './photoCompare';

// Feature 003 Phase 4+5+10 additions (additive — preserve existing behavior)
export { searchLegalMA, mapLegalFindingToDbRow } from './legalScrape';
export { verifyAuditChain, computeHashLocal } from './auditChain';
export { formatPhotoMatchExplanation, classifyFaceVerification } from './photoCompare';
export { maigretDiscover, mapMaigretFindingToDbRow } from './maigretDiscovery';

// G-006: Field yang punya elevated handling (UU PDP — kontak + lokasi sensitif).
// Audit log on read di getFindings; UI render badge merah/oranye di OsintTab.
const ELEVATED_FIELDS = ['phone', 'phone_number', 'post_tagged_location'];

// Feature flags — control via env atau admin config
const SOCIAL_MAPPER_ENABLED = import.meta.env.VITE_SOCIAL_MAPPER_ENABLED === 'true';
const BRAND24_ENABLED       = import.meta.env.VITE_BRAND24_ENABLED === 'true';

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Stage 1 — Discovery. Cari profile candidates di sosial media.
 * Operator akan REVIEW + pilih kandidat sebelum Stage 2.
 *
 * @param {object} subject - { nik, nama_lengkap, foto_url, username? }
 * @param {string} operatorId
 * @param {string[]} platforms - ['facebook','instagram','tiktok','x','linkedin']
 * @returns {Promise<{run_id, candidates, error?}>}
 */
export async function discoverCandidates(subject, operatorId, platforms = ['facebook','instagram','tiktok','x','linkedin']) {
  if (!subject?.nik || !operatorId) {
    return { error: 'NIK subject + operator ID wajib' };
  }

  // 1. Buat run record
  const { data: run, error: runErr } = await supabase
    .from('osint_runs')
    .insert({
      nik_subject:  subject.nik,
      operator_id:  operatorId,
      stage:        'stage_1_running',
      adapters:     SOCIAL_MAPPER_ENABLED ? ['social_mapper'] : ['apify'],
      status:       'pending',
    })
    .select()
    .single();

  if (runErr || !run) return { error: 'Gagal buat run: ' + (runErr?.message || 'unknown') };

  // 2. Audit log
  await logAudit('run_started', operatorId, subject.nik, {
    run_id: run.id, platforms, adapter: SOCIAL_MAPPER_ENABLED ? 'social_mapper' : 'apify',
  });

  try {
    // 3. Discovery — pakai Social Mapper jika enabled, fallback ke Apify
    const discoverFn = SOCIAL_MAPPER_ENABLED ? socialMapperDiscover : apifyDiscover;
    const rawCandidates = await discoverFn(subject, platforms);

    // 4. Score candidates
    const scored = await Promise.all(
      rawCandidates.map(async (c) => ({
        ...c,
        ...(await scoreCandidate(subject, c)),
      }))
    );

    // 5. Insert candidates ke DB (kalau ada hasil)
    let candidates = [];
    if (scored.length > 0) {
      const candidatesToInsert = scored
        .filter(c => c.url) // skip kalau URL kosong (tidak valid candidate)
        .map((c) => ({
          run_id:           run.id,
          nik_subject:      subject.nik,
          platform:         c.platform,
          profile_handle:   c.handle,
          profile_url:      c.url,
          display_name:     c.display_name,
          thumbnail_url:    c.thumbnail_url,
          confidence:       c.confidence,
          match_signals:    c.match_signals,
          photos_collected: 0,
        }));

      if (candidatesToInsert.length > 0) {
        const { data, error: candErr } = await supabase
          .from('osint_candidates')
          .insert(candidatesToInsert)
          .select();
        if (candErr) throw new Error('Gagal simpan candidates: ' + candErr.message);
        candidates = data || [];
      }
    }

    // 6. Update run → awaiting_selection (bahkan kalau 0 candidates, supaya UI tampil empty state)
    await supabase
      .from('osint_runs')
      .update({ stage: 'awaiting_selection', findings_count: candidates.length })
      .eq('id', run.id);

    await logAudit('stage_1_complete', operatorId, subject.nik, {
      run_id: run.id, candidates_found: candidates.length,
    });

    return { run_id: run.id, candidates };
  } catch (err) {
    await supabase.from('osint_runs').update({
      stage: 'failed', status: 'error', error_message: err.message, completed_at: new Date().toISOString(),
    }).eq('id', run.id);
    await logAudit('run_failed', operatorId, subject.nik, { run_id: run.id, error: err.message });
    return { error: err.message, run_id: run.id };
  }
}

/**
 * Stage 2 — Enrichment. Pull data dari kandidat yang DIPILIH operator.
 *
 * @param {string} runId
 * @param {string[]} candidateIds - hanya kandidat dengan is_selected=true
 * @param {string[]} dataTypes - ['photos','posts','mentions','public_records']
 * @param {string} operatorId
 * @returns {Promise<{findings, error?}>}
 */
export async function enrichCandidates(runId, candidateIds, dataTypes, operatorId) {
  if (!runId || !candidateIds?.length) return { error: 'Run ID + candidate IDs wajib' };

  // 1. Mark candidates as selected
  await supabase
    .from('osint_candidates')
    .update({ is_selected: true })
    .in('id', candidateIds);

  // 2. Update run stage
  await supabase
    .from('osint_runs')
    .update({ stage: 'stage_2_running', adapters: ['apify'] })
    .eq('id', runId);

  // 3. Fetch selected candidates
  const { data: candidates } = await supabase
    .from('osint_candidates')
    .select('*')
    .in('id', candidateIds);

  if (!candidates?.length) return { error: 'Candidates tidak ditemukan' };

  const nik = candidates[0].nik_subject;
  await logAudit('candidate_selected', operatorId, nik, {
    run_id: runId, candidate_count: candidateIds.length, data_types: dataTypes,
  });

  // Subject biometric dari Datacenter — embedding pre-computed lebih cepat &
  // canonical (hasil enrollment KTP) dibanding re-extract dari foto setiap call.
  const { data: subjectRow } = await supabase
    .from('penduduk')
    .select('foto_url, face_embedding')
    .eq('nik', nik)
    .single();
  const subjectForCompare = {
    foto_url:       subjectRow?.foto_url || null,
    face_embedding: subjectRow?.face_embedding || null,
  };

  try {
    // 4. Run enrichment parallel per candidate (Promise.allSettled — 1 fail
    //    tidak block kandidat lain). apifyEnrich pilih actor per platform
    //    via STAGE2_ACTOR_MAP di actorConfig.js.
    const enrichmentResults = await Promise.allSettled(
      candidates.map((c) => apifyEnrich(c, dataTypes))
    );

    // 5. Collect findings + G-001 photo compare per kandidat
    const allFindings = [];
    for (let i = 0; i < enrichmentResults.length; i++) {
      const result = enrichmentResults[i];
      if (result.status !== 'fulfilled') continue;
      const candidate = candidates[i];

      // G-001: Aggregate photo URLs dari semua finding kandidat ini, lalu
      // compare ke subject photo. Hasil masuk ke match_signals + confidence.
      const photoUrls = result.value
        .flatMap(f => f.raw_data?.image_urls || (f.raw_data?.profile_pic ? [f.raw_data.profile_pic] : []))
        .filter(Boolean);
      let photoCompareResult = null;
      if (photoUrls.length > 0 && (subjectForCompare.foto_url || subjectForCompare.face_embedding)) {
        photoCompareResult = await comparePhotosWithSubject(photoUrls, subjectForCompare, 20);
      }

      for (const finding of result.value) {
        // G-006: Detect elevated fields di raw_data — flag is_elevated true
        // kalau ada minimal 1 field sensitif (phone/post_tagged_location).
        const raw = finding.raw_data || {};
        const elevatedFound = ELEVATED_FIELDS.filter(f => raw[f] != null && raw[f] !== '');
        const isElevated = elevatedFound.length > 0;

        // Merge photo compare ke match_signals kalau ada (G-001 result).
        const baseSignals = finding.match_signals || candidate.match_signals || {};
        const mergedSignals = photoCompareResult ? {
          ...baseSignals,
          photo_match:        photoCompareResult.best_similarity,
          photo_match_count:  photoCompareResult.match_count,
          photo_total_count:  photoCompareResult.total_count,
          photo_attempted:    photoCompareResult.attempted,
        } : baseSignals;

        const baseExplain = finding.signal_explain || '';
        const photoExplainSuffix = photoCompareResult && photoCompareResult.match_count > 0
          ? ` · ${photoCompareResult.explanation}`
          : '';

        allFindings.push({
          run_id:         runId,
          nik_subject:    nik,
          adapter:        'apify',
          source_url:     finding.url,
          source_type:    finding.source_type || 'social_media',
          platform:       finding.platform,
          confidence:     finding.confidence || candidate.confidence,
          match_signals:  mergedSignals,
          signal_explain: (baseExplain + photoExplainSuffix).slice(0, 480),
          raw_data:       { ...raw, is_elevated: isElevated, elevated_fields: elevatedFound },
        });
      }
    }

    // Per spec § 4.6 item 20: de-dedup on re-run, keyed by source_url + nik_subject.
    // Existing findings di-UPDATE timestamps + confidence, baru INSERT yang truly new.
    if (allFindings.length > 0) {
      // Fetch existing findings untuk subject ini (by source_url)
      const sourceUrls = [...new Set(allFindings.map(f => f.source_url).filter(Boolean))];
      const { data: existing } = await supabase
        .from('osint_findings')
        .select('id, source_url')
        .eq('nik_subject', nik)
        .in('source_url', sourceUrls);
      const existingMap = new Map((existing || []).map(e => [e.source_url, e.id]));

      const toUpdate = [];
      const toInsert = [];
      for (const f of allFindings) {
        if (existingMap.has(f.source_url)) {
          toUpdate.push({ id: existingMap.get(f.source_url), ...f });
        } else {
          toInsert.push(f);
        }
      }

      // Update existing — refresh timestamps + confidence (clears stale flag)
      for (const u of toUpdate) {
        await supabase.from('osint_findings').update({
          retrieved_at:    new Date().toISOString(),
          expires_at:      new Date(Date.now() + 24 * 30 * 86400000).toISOString(),
          confidence:      u.confidence,
          match_signals:   u.match_signals,
          signal_explain:  u.signal_explain,
          raw_data:        u.raw_data,
        }).eq('id', u.id);
      }

      // Insert truly new findings
      if (toInsert.length > 0) {
        await supabase.from('osint_findings').insert(toInsert);
      }

      console.log(`[osint] dedup: ${toUpdate.length} updated, ${toInsert.length} new`);

      // Audit: findings_persisted (event ke-5 dari 7 wajib per spec § 4.9)
      const elevatedCount = allFindings.filter(f => f.raw_data?.is_elevated).length;
      await logAudit('findings_persisted', operatorId, nik, {
        run_id:           runId,
        saved_count:      toInsert.length + toUpdate.length,
        new_count:        toInsert.length,
        updated_count:    toUpdate.length,
        elevated_count:   elevatedCount,
      });
    }

    // 6. Mark complete
    await supabase
      .from('osint_runs')
      .update({
        stage:           'complete',
        status:          'success',
        completed_at:    new Date().toISOString(),
        findings_count:  allFindings.length,
      })
      .eq('id', runId);

    await logAudit('stage_2_complete', operatorId, nik, {
      run_id: runId, findings_count: allFindings.length,
    });

    return { findings: allFindings };
  } catch (err) {
    await supabase
      .from('osint_runs')
      .update({ stage: 'failed', status: 'error', error_message: err.message })
      .eq('id', runId);
    await logAudit('enrichment_failed', operatorId, nik, { run_id: runId, error: err.message });
    return { error: err.message };
  }
}

/**
 * Polling status untuk async operations.
 */
export async function getRunStatus(runId) {
  const { data, error } = await supabase
    .from('osint_runs')
    .select('*')
    .eq('id', runId)
    .single();
  if (error) return { error: error.message };
  return { run: data };
}

/**
 * Get saved findings untuk subject — untuk UI display.
 * is_stale dihitung di runtime (retrieved_at < now - 90 days), bukan column DB.
 *
 * G-006: Elevated data audit on read — kalau findings yang di-fetch contain
 * elevated fields (phone/location), log ke osint_audit dengan operator_id +
 * field name supaya UU PDP audit trail komplit.
 */
export async function getFindings(nik, includeStale = false, operatorId = null) {
  let query = supabase
    .from('osint_findings')
    .select('*')
    .eq('nik_subject', nik)
    .eq('is_saved', true)
    .eq('is_wrong_match', false);
  if (!includeStale) {
    const cutoff = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString();
    query = query.gte('retrieved_at', cutoff);
  }
  const { data, error } = await query.order('retrieved_at', { ascending: false });
  if (error) return { error: error.message, findings: [] };
  // Tambah is_stale flag di runtime + filter out legacy mock findings
  // (DB punya mock findings dari pre-real-Apify testing — `raw_data.mock = true`).
  const STALE_MS = 90 * 24 * 3600 * 1000;
  const findings = (data || [])
    .filter(f => !(f.raw_data?.mock === true))
    .map(f => ({
      ...f,
      is_stale: (Date.now() - new Date(f.retrieved_at).getTime()) > STALE_MS,
    }));

  // G-006: Audit on read — log elevated fields yang di-fetch operator.
  // Best-effort fire-and-forget; tidak block render kalau audit gagal.
  if (operatorId) {
    const elevatedFindings = findings.filter(f => f.raw_data?.is_elevated === true);
    if (elevatedFindings.length > 0) {
      const fieldsRead = [...new Set(
        elevatedFindings.flatMap(f => f.raw_data?.elevated_fields || [])
      )];
      logAudit('elevated_field_read', operatorId, nik, {
        finding_count: elevatedFindings.length,
        fields:        fieldsRead,
      }).catch(() => {}); // silent — audit failure tidak boleh break UX
    }
  }

  return { findings };
}

/**
 * Get summary cepat untuk Subject Profiling header.
 */
export async function getSummary(nik) {
  const { data, error } = await supabase.rpc('get_osint_summary', { p_nik: nik });
  if (error) return { error: error.message };
  return data?.[0] || null;
}

/**
 * Get candidates dari run tertentu (untuk awaiting_selection state).
 */
export async function getCandidates(runId) {
  const { data, error } = await supabase
    .from('osint_candidates')
    .select('*')
    .eq('run_id', runId)
    .order('confidence', { ascending: false });
  if (error) return { error: error.message, candidates: [] };
  return { candidates: data || [] };
}

/**
 * Toggle finding saved/wrong-match flag (operator action).
 */
export async function flagFinding(findingId, action, operatorId, nik) {
  const updates =
    action === 'save'         ? { is_saved: true,  is_wrong_match: false }
  : action === 'unsave'       ? { is_saved: false }
  : action === 'wrong_match'  ? { is_wrong_match: true, is_saved: false }
  : null;
  if (!updates) return { error: 'Invalid action' };

  const { error } = await supabase
    .from('osint_findings')
    .update(updates)
    .eq('id', findingId);
  if (error) return { error: error.message };

  await logAudit(`finding_${action}`, operatorId, nik, { finding_id: findingId });
  return { success: true };
}

/**
 * Admin purge — permanent delete semua data OSINT untuk subject.
 */
export async function purgeSubject(nik, operatorId, reason = null) {
  const { data, error } = await supabase.rpc('purge_osint_subject', {
    p_nik: nik, p_operator: operatorId, p_reason: reason,
  });
  if (error) return { error: error.message };
  const result = data?.[0] || { runs_deleted: 0, candidates_deleted: 0, findings_deleted: 0 };
  // Audit: purge_executed (event ke-7 dari 7 wajib per spec § 4.9)
  await logAudit('purge_executed', operatorId, nik, {
    scope:              'all_osint_data',
    runs_deleted:       result.runs_deleted,
    candidates_deleted: result.candidates_deleted,
    findings_deleted:   result.findings_deleted,
    reason,
  });
  return result;
}

// ============================================================================
// HELPERS
// ============================================================================

async function logAudit(action, operatorId, subjectId, metadata = {}) {
  try {
    await supabase.from('osint_audit').insert({
      action, operator_id: operatorId, subject_id: subjectId, metadata,
    });
  } catch (err) {
    console.warn('[osint] audit log gagal:', err.message);
  }
}

// ============================================================================
// FEATURE FLAGS — exposed untuk UI gating
// ============================================================================
export const FEATURE_FLAGS = {
  SOCIAL_MAPPER_ENABLED,
  BRAND24_ENABLED,
};
